import { useEffect } from 'react'
import Modal from 'src/components/Modal'
import ModalHeader from 'src/components/Modal/ModalHeader'
import ModalContent from 'src/components/Modal/ModalContent'
import useModal from 'src/hooks/useModal'
import { Typography, Fade } from '@mui/material'
import Markdown from 'react-markdown'
import { gql, useQuery } from '@apollo/client'
import ExcruciatinglyIndeterminateProgress from 'src/components/ExcruciatinglyIndeterminateProgress'

const CREW_JOB_QUERY = gql`
  query CrewJob($id: String!) {
    crewJob(id: $id) {
      status
      result {
        raw
      }
    }
  }
`

const AiAnswerModal = () => {
  const {
    data: { jobId, prompt },
  } = useModal('aiAnswerModal')

  const { data, loading, startPolling, stopPolling } = useQuery(
    CREW_JOB_QUERY,
    {
      variables: { id: jobId },
      skip: !jobId,
      pollInterval: 1500,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const status = data?.crewJob?.status
        if (status === 'completed' || status === 'error') {
          stopPolling()
        }
      },
    }
  )

  // Compute loading, error, and result states based on Apollo loading or job status
  const status = data?.crewJob?.status
  const showLoading = loading || status === 'pending' || status === 'running'
  const showError = status === 'error'
  const showResult = status === 'completed'

  useEffect(() => {
    if (!jobId) return
    if (status === 'completed' || status === 'error') {
      stopPolling()
    } else if (status === 'pending' || status === 'running') {
      startPolling(1500)
    }
    return () => {
      stopPolling()
    }
  }, [jobId, status])

  // Helper to key the fade transition
  const getContentKey = (
    showLoading: boolean,
    showError: boolean,
    showResult: boolean
  ) => {
    if (showLoading) return 'loading'
    if (showError) return 'error'
    if (showResult) return 'result'
    return 'empty'
  }

  const contentKey = getContentKey(showLoading, showError, showResult)

  return (
    <Modal
      modalName="aiAnswerModal"
      classes={{ paper: 'w-1/2' }}
      ModalProps={{ disableAutoFocus: true }}
    >
      <ModalHeader title={`Asking AI: ${prompt}`} />
      <ModalContent classes={{ root: 'px-3' }}>
        {/* <Typography variant="h6">Prompt: {prompt}</Typography> */}
        <div className="pb-4" style={{ minHeight: 80 }}>
          <Fade in={true} key={contentKey} timeout={500}>
            <div>
              {showLoading && (
                <ExcruciatinglyIndeterminateProgress loading={true} />
              )}
              {showError && (
                <div>Error occurred while fetching the answer.</div>
              )}
              {showResult && data?.crewJob?.result && (
                <>
                  <Typography variant="h6" className="mb-2">
                    Answer:
                  </Typography>
                  <Markdown
                    components={{ p: 'span' }}
                    className="rounded-lg bg-gray-100 p-4"
                  >
                    {data.crewJob.result.raw ?? 'No answer available.'}
                  </Markdown>
                </>
              )}
            </div>
          </Fade>
        </div>
      </ModalContent>
    </Modal>
  )
}

export default AiAnswerModal
